import { useEffect, useState, useMemo } from "react";
import { Download, Headphones, Video } from "lucide-react";
import {
	getOfflineVideos,
	removeOfflineVideo,
	getTotalStorageUsedMB,
	type OfflineVideoMetadata,
	getMediaType,
	isCompletedStatus,
} from "@/utils/storage/offlineVideosStorage";
import { formatDuration } from "@/utils/dateAndTime";
// import { isNativePlatform } from "@/utils/storage/storage";
import { VideoPlayer } from "@/pages/Workshop/VideoPlayer";
import { AudioPlayer } from "@/pages/Workshop/AudioPlayer";
// import { Link } from "@tanstack/react-router";
import { TrashIcon } from "@/components/icons/trash";
import { pauseDownload, cancelDownload } from "@/utils/videoDownloadService";
import { pauseAudioDownload, cancelAudioDownload } from "@/utils/audioDownloadService";
import { videoLog } from "@/components/VideoDownloadDebugger";
import { useOnlineStatusContext } from "@/context/OnlineStatusContext";
import { PlayIcon } from "@/components/icons/play";
import type { ProductDataMultimedia } from "./Workshop/MultimediaCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearch, useNavigate, useLocation } from "@tanstack/react-router";

// Interface for grouped media (video + audio of same material)
interface GroupedMedia {
	key: string;
	video: OfflineVideoMetadata | null;
	audio: OfflineVideoMetadata | null;
	productData: ProductDataMultimedia;
	courseClassesId: string;
	videoName: string;
	duration: number;
}

const checkProductType = (
	productData: ProductDataMultimedia | undefined
): string | null => {
	if (!productData) return null;

	const typeMap: Record<string, string | null> = {
		webinar: "Webinar •",
		training: "Szkolenie •",
		course: "Kurs •",
		unknown: null,
	};

	return typeMap[productData.productType] ?? null;
};

// Component for rendering a list of grouped media cards
const MediaCardList = ({
	mediaList,
	onPlayVideo,
	onPlayAudio,
	onDelete,
	checkProductType,
}: {
	mediaList: Array<GroupedMedia>;
	onPlayVideo: (video: OfflineVideoMetadata) => void;
	onPlayAudio: (audio: OfflineVideoMetadata) => void;
	onDelete: (videoId: string) => Promise<void>;
	checkProductType: (productData: ProductDataMultimedia | undefined) => string | null;
}): JSX.Element => {
	if (mediaList.length === 0) {
		return (
			<div className="text-center py-8 text-gray-400">
				<p>Brak materiałów w tej kategorii</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{mediaList.map((group) => (
				<div
					key={group.key}
					className="bg-white p-5 pb-2 rounded-lg shadow-md"
				>
					{/* Header */}
					<div className="flex justify-between items-start mb-2">
						<div className="flex-1">
							<h3 className="text-md">{group.videoName}</h3>
							{group.productData && (
								<>
									<p className="text-sm text-gray-500 mt-5">
										{checkProductType(group.productData)}{" "}
										{group.productData.productName}
									</p>
									<p className="text-sm text-gray-500 mt-1">
										{group.productData.authors.length > 0 &&
											group.productData.authors
												.map((author) => author.display_name)
												.join(", ")}
									</p>
								</>
							)}
						</div>
					</div>

					{/* Media info */}
					<div className="flex items-center flex-wrap gap-2 text-xs text-gray-600 mt-6 mb-3">
						<span>{formatDuration(group.duration)}</span>
						{group.video && (
							<span className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
								<Video className="w-3 h-3" />
								{group.video.selectedResolution} • {group.video.fileSizeMB} MB
							</span>
						)}
						{group.audio && (
							<span className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded">
								<Headphones className="w-3 h-3" />
								Audio • {group.audio.fileSizeMB} MB
							</span>
						)}
					</div>

					{/* Actions */}
					<div className="border-t border-gray-100 pt-2 mt-2">
						{/* Video actions */}
						{group.video && (
							<div className="flex justify-between items-center py-2">
								<button
									onClick={async () => {
										await onDelete(group.video!.videoId);
									}}
									className="flex items-center gap-2 text-gray-500 hover:text-red-600"
								>
									<TrashIcon />
									<span className="text-sm underline">Usuń video</span>
								</button>
								<div className="flex items-center">
									<p className="mr-2 text-sm">Oglądaj offline</p>
									<button
										className="p-2"
										onClick={() => {
											onPlayVideo(group.video!);
										}}
									>
										<PlayIcon />
									</button>
								</div>
							</div>
						)}

						{/* Audio actions */}
						{group.audio && (
							<div className="flex justify-between items-center py-2">
								<button
									onClick={async () => {
										await onDelete(group.audio!.videoId);
									}}
									className="flex items-center gap-2 text-gray-500 hover:text-red-600"
								>
									<TrashIcon />
									<span className="text-sm underline">Usuń audio</span>
								</button>
								<div className="flex items-center">
									<p className="mr-2 text-sm">Słuchaj offline</p>
									<button
										className="p-2"
										onClick={() => {
											onPlayAudio(group.audio!);
										}}
									>
										<Headphones className="w-6 h-6 text-purple-600" />
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

const OfflineVideosPage = (): JSX.Element => {
	const [videos, setVideos] = useState<Array<OfflineVideoMetadata>>([]);
	const [totalStorageMB, setTotalStorageMB] = useState(0);
	const [loading, setLoading] = useState(true);
	const [selectedVideo, setSelectedVideo] =
		useState<OfflineVideoMetadata | null>(null);
	const [selectedAudio, setSelectedAudio] =
		useState<OfflineVideoMetadata | null>(null);
	const { isOnline } = useOnlineStatusContext();

	// ⭐ Dodaj routing hooks
	const search = useSearch({ from: "/(app)/_app/offline-videos" });
	const navigate = useNavigate();
	const location = useLocation();

	// Pobierz productType z URL (default "training")
	const currentProductType = search.productType || "training";

	const loadVideos = async (): Promise<void> => {
		try {
			videoLog.info("OfflinePage", "Loading offline videos list", {});

			const videosData = await getOfflineVideos();
			const videosList = Object.values(videosData).sort((a, b) => {
				return (b.downloadCompletedAt || 0) - (a.downloadCompletedAt || 0);
			});
			setVideos(videosList);

			const total = await getTotalStorageUsedMB();
			setTotalStorageMB(total);

			videoLog.success(
				"OfflinePage",
				`Loaded ${videosList.length} videos, ${total.toFixed(2)} MB total`,
				{
					videoCount: videosList.length,
					totalStorageMB: total.toFixed(2),
				}
			);
		} catch (error) {
			console.error("Error loading offline videos:", error);
			videoLog.error("OfflinePage", "Error loading offline videos", {
				error: error instanceof Error ? error.message : String(error),
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadVideos().catch(console.error);
	}, []);

	// Okresowe sprawdzanie postępu podczas pobierania
	useEffect(() => {
		const downloadingVideos = videos.filter((v) => v.status === "downloading");
		if (downloadingVideos.length === 0) return;

		const intervalId = setInterval(() => {
			void (async (): Promise<void> => {
				// Pobierz aktualne dane z storage
				const videosData = await getOfflineVideos();
				const updatedVideos = Object.values(videosData).sort((a, b) => {
					return (b.downloadCompletedAt || 0) - (a.downloadCompletedAt || 0);
				});

				// Sprawdź czy są zmiany w postępie
				const hasChanges = updatedVideos.some((video) => {
					const currentVideo = videos.find((v) => v.videoId === video.videoId);
					return (
						currentVideo &&
						(currentVideo.downloadProgress !== video.downloadProgress ||
							currentVideo.status !== video.status)
					);
				});

				if (hasChanges) {
					setVideos(updatedVideos);

					// Aktualizuj też całkowite użycie storage
					const total = await getTotalStorageUsedMB();
					setTotalStorageMB(total);
				}
			})();
		}, 1000);

		return (): void => {
			clearInterval(intervalId);
		};
	}, [videos]); // Zależność od videos, żeby interval się restartował gdy lista się zmieni

	const handleDelete = async (videoId: string): Promise<void> => {
		if (confirm("Czy na pewno chcesz usunąć to wideo?")) {
			videoLog.warning(
				"OfflinePage",
				"User confirmed delete video",
				{ videoId },
				videoId
			);

			try {
				await removeOfflineVideo(videoId);
				videoLog.success(
					"OfflinePage",
					"Video deleted successfully",
					{ videoId },
					videoId
				);
				await loadVideos();
			} catch (error) {
				console.error("Error deleting video:", error);
				videoLog.error(
					"OfflinePage",
					"Error deleting video",
					{
						error: error instanceof Error ? error.message : String(error),
						videoId,
					},
					videoId
				);
				alert("Błąd podczas usuwania wideo");
			}
		} else {
			videoLog.info(
				"OfflinePage",
				"User canceled delete",
				{ videoId },
				videoId
			);
		}
	};

	const handlePlay = (video: OfflineVideoMetadata): void => {
		videoLog.info(
			"OfflinePage",
			"User started playback",
			{
				videoName: video.videoName,
				videoSource: video.videoSource,
				vimeoId: video.vimeoId,
				originalVideoUrl: video.originalVideoUrl,
			},
			video.videoId
		);

		console.log("🎬 [OfflineVideosPage] Starting video playback:", {
			videoId: video.videoId,
			videoName: video.videoName,
			videoSource: video.videoSource,
			vimeoId: video.vimeoId,
			originalVideoUrl: video.originalVideoUrl,
			productData: video.productData,
			courseClassesId: video.courseClassesId,
		});

		setSelectedVideo(video);
	};

	const handlePauseDownload = async (
		media: OfflineVideoMetadata
	): Promise<void> => {
		const mediaType = getMediaType(media);
		videoLog.warning(
			"OfflinePage",
			`User paused ${mediaType} download from list`,
			{
				videoName: media.videoName,
				currentProgress: media.downloadProgress,
				mediaType,
			},
			media.videoId
		);

		try {
			if (mediaType === "audio") {
				await pauseAudioDownload(
					media.productData.productType,
					media.productData.productId,
					media.courseClassesId
				);
			} else {
				await pauseDownload(
					media.productData.productType,
					media.productData.productId,
					media.courseClassesId
				);
			}
			videoLog.success(
				"OfflinePage",
				"Download paused successfully",
				{},
				media.videoId
			);
			await loadVideos();
		} catch (error) {
			console.error("Error pausing download:", error);
			videoLog.error(
				"OfflinePage",
				"Error pausing download",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				media.videoId
			);
			alert("Błąd podczas wstrzymywania pobierania");
		}
	};
	
	const handlePlayAudio = (audio: OfflineVideoMetadata): void => {
		videoLog.info(
			"OfflinePage",
			"User started audio playback",
			{
				audioName: audio.videoName,
			},
			audio.videoId
		);

		console.log("🎵 [OfflineVideosPage] Starting audio playback:", {
			audioId: audio.videoId,
			audioName: audio.videoName,
			productData: audio.productData,
			courseClassesId: audio.courseClassesId,
		});

		setSelectedAudio(audio);
	};

	const handleResume = (video: OfflineVideoMetadata): void => {
		// Redirect to workshop page to resume download
		alert(
			`Przejdź do kursu "${video.productData?.productName}" aby wznowić pobieranie`
		);
	};

	// Calculate completed media - must be before any early returns
	const completedMedia = videos.filter((v) => isCompletedStatus(v.status));
	
	// Group completed media by productId + courseClassesId - MUST be before early returns
	const groupedMedia = useMemo(() => {
		const groups: Record<string, GroupedMedia> = {};
		
		for (const media of completedMedia) {
			const key = `${media.productData.productId}_${media.courseClassesId}`;
			const mediaType = getMediaType(media);
			
			if (!groups[key]) {
				groups[key] = {
					key,
					video: null,
					audio: null,
					productData: media.productData,
					courseClassesId: media.courseClassesId,
					videoName: media.videoName,
					duration: media.duration,
				};
			}
			
			if (mediaType === "audio") {
				groups[key].audio = media;
			} else {
				groups[key].video = media;
			}
		}
		
		return Object.values(groups).sort((a, b) => {
			const aTime = Math.max(
				a.video?.downloadCompletedAt || 0,
				a.audio?.downloadCompletedAt || 0
			);
			const bTime = Math.max(
				b.video?.downloadCompletedAt || 0,
				b.audio?.downloadCompletedAt || 0
			);
			return bTime - aTime;
		});
	}, [completedMedia]);
	
	// Filter grouped media by product type
	const trainingMedia = groupedMedia.filter(
		(g) => g.productData.productType === "training"
	);
	const webinarMedia = groupedMedia.filter(
		(g) => g.productData.productType === "webinar"
	);
	const courseMedia = groupedMedia.filter(
		(g) => g.productData.productType === "course"
	);

	const downloadingVideos = videos.filter(
		(v) => v.status === "downloading" || v.status === "paused"
	);

	if (loading) {
		return (
			<div className="p-4">
				<p>Ładowanie...</p>
			</div>
		);
	}

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			<div className="bg-blue-50 p-4 rounded-lg mb-6">
				<div className="flex items-center justify-between gap-4">
					<p className="text-sm text-gray-600 flex-1 min-w-0">
						Całkowite miejsce na dysku urządzenia zajmowane przez pobrane materiały
					</p>
					<p className="text-xl font-medium whitespace-nowrap flex-shrink-0">
						{totalStorageMB.toFixed(2)} MB
					</p>
				</div>
				<p className="text-xs text-gray-500 mt-4">
					Pobrane materiały: {completedMedia.length} (video: {completedMedia.filter(m => getMediaType(m) === "video").length}, audio: {completedMedia.filter(m => getMediaType(m) === "audio").length})
				</p>
			</div>

			{isOnline && downloadingVideos.length > 0 && (
				<div className="mb-6 space-y-3">
					{downloadingVideos.map((media) => {
						const mediaType = getMediaType(media);
						return (
							<div key={media.videoId} className="bg-white rounded-lg shadow-md">
								<div className="flex items-center gap-2 px-5 pt-5 mb-3">
									{mediaType === "audio" ? (
										<Headphones className="w-5 h-5 text-purple-600" />
									) : (
										<Video className="w-5 h-5 text-blue-600" />
									)}
									<h3 className="text-md">{media.videoName}</h3>
								</div>

								{/* Status i przyciski */}
								<div className="flex items-center justify-between px-5 pb-4">
									<p className="text-sm text-gray-400">
										{media.status === "paused"
											? "Pobieranie wstrzymane"
											: `Trwa pobieranie ${mediaType === "audio" ? "audio" : "video"}... ${Math.round(media.downloadProgress)}%`}
									</p>
									<div className="flex gap-2">
										{/* Wstrzymaj */}
										{media.status === "downloading" && (
											<button
												onClick={async () => {
													await handlePauseDownload(media);
												}}
												className="text-sm text-orange-600 underline hover:text-orange-800"
											>
												Wstrzymaj
											</button>
										)}

										{/* Wznów */}
										{media.status === "paused" && (
											<button
												onClick={() => {
													handleResume(media);
												}}
												className="text-sm text-blue-600 underline hover:text-blue-800"
											>
												Wznów
											</button>
										)}

										{/* Anuluj */}
										<button
											onClick={async () => {
												if (
													confirm("Czy na pewno chcesz anulować to pobieranie?")
												) {
													try {
														if (mediaType === "audio") {
															await cancelAudioDownload(
																media.productData.productType,
																media.productData.productId,
																media.courseClassesId
															);
														} else {
															await cancelDownload(
																media.productData.productType,
																media.productData.productId,
																media.courseClassesId
															);
														}
														await loadVideos(); // Reload list
													} catch (error) {
														console.error("Cancel error:", error);
														alert("Błąd podczas anulowania");
													}
												}
											}}
											className="text-sm text-red-600 underline hover:text-red-800"
										>
											Anuluj
										</button>
									</div>
								</div>

								{/* Progress bar */}
								<div className={`h-[5px] w-full ${mediaType === "audio" ? "bg-purple-200" : "bg-[#D5E5FF]"} rounded-b-xl overflow-hidden relative`}>
									{Math.round(media.downloadProgress) > 0 && (
										<div
											className={`h-full ${mediaType === "audio" ? "bg-purple-600" : "bg-[#196DFE]"} transition-all duration-300 ease-out`}
											style={{ width: `${Math.round(media.downloadProgress)}%` }}
										/>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* {pausedVideos.length > 0 && (
				<div className="mb-6">
					<h3 className="text-md font-semibold mb-2">Wstrzymane pobierania</h3>
					{pausedVideos.map((video) => (
						<div
							key={video.videoId}
							className="bg-gray-50 p-4 rounded-lg mb-2"
						>
							<p className="font-medium">{video.videoName}</p>
							<p className="text-xs text-gray-500 mt-1">{video.courseName}</p>
							<div className="flex gap-2 mt-2">
								<button
									onClick={() => {
										handleResume(video);
									}}
									className="text-sm text-blue-600 underline"
								>
									Wznów pobieranie
								</button>
								<button
									onClick={async () => {
										await handleDelete(video.videoId);
									}}
									className="text-sm text-red-600 underline"
								>
									Usuń
								</button>
							</div>
						</div>
					))}
				</div>
			)} */}

			{completedMedia.length === 0 ? (
				<div className="text-center py-12">
					<Download className="w-16 h-16 text-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">Brak pobranych materiałów</p>
					<p className="text-sm text-gray-400 mt-2">
						Pobierz materiały z lekcji, aby korzystać z nich offline
					</p>
				</div>
			) : (
				<Tabs className="w-full pb-4" value={currentProductType}>
					<TabsList className="w-full">
						<TabsTrigger
							className="w-1/3 p-2"
							value="training"
							onClick={async () => {
								await navigate({
									to: location.pathname,
									search: { productType: "training" },
									replace: true,
								});
							}}
						>
							Szkolenia
						</TabsTrigger>
						<TabsTrigger
							className="w-1/3 p-2"
							value="webinar"
							onClick={async () => {
								await navigate({
									to: location.pathname,
									search: { productType: "webinar" },
									replace: true,
								});
							}}
						>
							Webinary
						</TabsTrigger>
						<TabsTrigger
							className="w-1/3 p-2"
							value="course"
							onClick={async () => {
								await navigate({
									to: location.pathname,
									search: { productType: "course" },
									replace: true,
								});
							}}
						>
							Kursy
						</TabsTrigger>
					</TabsList>

					<TabsContent value="training" className="mt-4">
						<MediaCardList
							mediaList={trainingMedia}
							onPlayVideo={handlePlay}
							onPlayAudio={handlePlayAudio}
							onDelete={handleDelete}
							checkProductType={checkProductType}
						/>
					</TabsContent>

					<TabsContent value="webinar" className="mt-4">
						<MediaCardList
							mediaList={webinarMedia}
							onPlayVideo={handlePlay}
							onPlayAudio={handlePlayAudio}
							onDelete={handleDelete}
							checkProductType={checkProductType}
						/>
					</TabsContent>

					<TabsContent value="course" className="mt-4">
						<MediaCardList
							mediaList={courseMedia}
							onPlayVideo={handlePlay}
							onPlayAudio={handlePlayAudio}
							onDelete={handleDelete}
							checkProductType={checkProductType}
						/>
					</TabsContent>
				</Tabs>
			)}

			{/* Video Player Modal */}
			{selectedVideo && (
				<div className="fixed inset-0 z-50 bg-black">
					<VideoPlayer
						videoContent={{
							video_source: "local_memory",
						}}
						open="VIDEO"
						productData={selectedVideo.productData}
						courseClassesId={selectedVideo.courseClassesId}
						setOpen={(open) => {
							if (!open) {
								setSelectedVideo(null);
							}
						}}
					/>
				</div>
			)}

			{/* Audio Player Modal */}
			{selectedAudio && (
				<div className="fixed inset-0 z-50 bg-black">
					<AudioPlayer
						audioContent={{
							audio_source: "local_memory",
						}}
						open="AUDIO_V2"
						productData={selectedAudio.productData}
						courseClassesId={selectedAudio.courseClassesId}
						setOpen={(open) => {
							if (!open) {
								setSelectedAudio(null);
							}
						}}
					/>
				</div>
			)}
		</main>
	);
};

export default OfflineVideosPage;
